import {
    bindPodcastRepeatOff,
    type CloudQueueControllerDeps,
    CloudQueueControllerImpl,
    getStreamingSettingsController,
    localPlaybackRatePreference,
    type ManagedPlaylistController,
    type Player,
    PlaylistControllerImpl,
    SleepTimerController,
} from '@amzn/amp-playback'
import type { BridgedPlayback } from '@amzn/amp-playback/bridge'
import {
    type Container,
    createContainer,
    externalDependencies,
    type Factories,
    validateFactories,
} from '@amazon/vinyl-di'
import type { AppViewDeps } from '@/AppView'
import { createPlayer } from '@/playback/playerFactory'
import { LOCAL_PLAYBACK, type PlaybackSelection } from '@/playback/playbackSelection'
import { createGoodNetworkObservable } from '@/offline/createGoodNetworkObservable'
import {
    PartySessionControllerImpl,
    type PartySessionControllerImplDeps,
} from '@/party/PartySessionController'

/**
 * Interface for application dependencies with lazy-loaded player
 */
export type AppDependencies = AppViewDeps

type PlaybackDeps = Pick<
    AppViewDeps,
    | 'playerPromise'
    | 'playlistController'
    | 'cloudQueueController'
    | 'sleepTimer'
    | 'streamingSettings'
    | 'playbackRatePreference'
>

function localPlaybackFactories(container: () => Container<AppDependencies>) {
    return validateFactories({
        playerPromise: (deps: { readonly playlistController: ManagedPlaylistController }, add) =>
            createPlayer({
                deps,
                // The session the player hosts needs the cloud queue controller and the sleep
                // timer, which themselves await this player, so they are read past this factory.
                cloudQueueController: () => container().dependencies.cloudQueueController,
                sleepTimer: () => container().dependencies.sleepTimer,
                add,
            }),

        playlistController: (): ManagedPlaylistController => new PlaylistControllerImpl(),

        cloudQueueController: (deps: CloudQueueControllerDeps, add) => {
            const controller = new CloudQueueControllerImpl(deps)
            add(
                bindPodcastRepeatOff({
                    playlistController: deps.playlistController,
                    modeWriter: controller.modeWriter,
                })
            )
            return controller
        },

        sleepTimer: (deps: { readonly playerPromise: Promise<Player> }) =>
            new SleepTimerController(() => deps.playerPromise),

        streamingSettings: getStreamingSettingsController,

        playbackRatePreference: () => localPlaybackRatePreference,
    } as const satisfies Factories<PlaybackDeps>)
}

/** The host's mirrors outlive each app reload, as the host's playback does. */
function bridgedPlaybackFactories(bridged: BridgedPlayback): Factories<PlaybackDeps> {
    return externalDependencies<PlaybackDeps>({
        playerPromise: Promise.resolve(bridged.player),
        playlistController: bridged.playlistController,
        cloudQueueController: bridged.cloudQueueController,
        sleepTimer: bridged.sleepTimer,
        streamingSettings: bridged.streamingSettings,
        playbackRatePreference: bridged.playbackRatePreference,
    })
}

export function createAppDependencies(
    playback: PlaybackSelection = LOCAL_PLAYBACK
): Container<AppDependencies> {
    const container: Container<AppDependencies> = createContainer(
        validateFactories({
            ...(playback.mode === 'bridged'
                ? bridgedPlaybackFactories(playback.bridged)
                : localPlaybackFactories(() => container)),

            goodNetwork$: createGoodNetworkObservable,

            partySessionController: (deps: PartySessionControllerImplDeps) =>
                new PartySessionControllerImpl(deps),
        } as const satisfies Factories<AppViewDeps>)
    )
    return container
}
